import React from "react";
import { observer } from "mobx-react";
import { computed, observable } from "mobx";
import { withRouter, RouteComponentProps } from "react-router-dom";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";

import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";

import { Button, IconButton, Typography } from "@material-ui/core";

import DayBannersStore from "stores/DayBannersStore";

import Container from "components/Layout/Container";
import Header from "components/Layout/Header";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import Page from "components/Layout/Page";
import ConfirmationDialog from "components/ConfirmationDialog";
import Placeholder from "components/Placeholder";
import PublishButton from "components/PublishButton";
import { SNACKBAR_TYPES } from "components/Snackbar";

import ImageForm from "./ImageForm";

import ROUTES from "navigation/routes";

import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import {
  BackButtonWrapper,
  ContainerHeader,
  DayContainer,
  Divider,
  Images,
  ImageContainer,
  IconControlsContainer,
  OverflowLoader,
  Wrapper,
  ScrollContainer,
} from "./styles";

type Props = GlobalPopupProps & SnackbarContextProps & RouteComponentProps & {};

@observer
class DayBanners extends React.Component<Props> {
  @observable isPopupDirty: boolean = false;
  @observable isDraggingGlobal: boolean = false;

  @computed get dayBanners() {
    return DayBannersStore.items;
  }

  componentDidMount() {
    DayBannersStore.fetch();
  }

  componentWillUnmount() {
    DayBannersStore.reset();
  }

  onChangeIsPopupDirty = ({ dirty }: { dirty: boolean }) => {
    this.isPopupDirty = dirty;
  };

  onBackClick = () => {
    this.props.history.push(ROUTES.IN_APP_TODAY);
  };

  onManageImage = ({
    dayTypeName,
    dayTypeId,
    imageId,
    imageUrl,
  }: {
    dayTypeName: string;
    dayTypeId?: number;
    imageId?: number;
    imageUrl?: string;
  }) => {
    const { openPopup } = this.props;

    openPopup({
      key: "ManageImage",
      canCloseByOuterClick: false,
      withoutExitButton: true,
      confirmation: {
        renderConfirmation: ({ deny, allow }) => (
          <ConfirmationDialog
            title="Discard Changes"
            text="Are you sure you would like to discard changes? All information will be lost."
            declineText="Cancel"
            acceptText="Discard"
            onDecline={deny}
            onAccept={allow}
          />
        ),
        shouldShowConfirmation: this.shouldShowConfirmation,
      },
      render: ({ close }) => (
        <ImageForm
          dayTypeId={dayTypeId}
          dayTypeName={dayTypeName}
          file={imageUrl}
          imageId={imageId}
          isLoading={DayBannersStore.isManaging}
          onSubmit={this.onManageImageSubmit(close)}
          onClose={close}
          onChangeIsDirty={this.onChangeIsPopupDirty}
        />
      ),
    });
  };

  onManageImageSubmit = (close: () => void) => async (values: any) => {
    const { setMessage } = this.props;
    const { file, id } = values;

    try {
      if (!id) {
        await DayBannersStore.addImage(values);
      } else {
        if (typeof file !== "string") {
          await DayBannersStore.editImage(values);
        }
      }

      this.resetIsPopupDirty();
      close();
      DayBannersStore.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onAddImage = ({
    dayTypeId,
    dayTypeName,
  }: {
    dayTypeId: number;
    dayTypeName: string;
  }) => () => {
    this.onManageImage({ dayTypeId, dayTypeName });
  };

  onEditImage = ({
    dayTypeName,
    imageId,
    imageUrl,
  }: {
    dayTypeName: string;
    imageId: number;
    imageUrl: string;
  }) => () => {
    this.onManageImage({ dayTypeName, imageUrl, imageId });
  };

  onDelete = ({ imageId }: { imageId: number }) => () => {
    const { openPopup } = this.props;

    openPopup({
      key: "DeleteImage",
      canCloseByOuterClick: true,
      withoutExitButton: true,
      render: ({ close }) => (
        <ConfirmationDialog
          title={"Delete Image"}
          text={"Are you sure you want to delete this image?"}
          acceptText={"Delete"}
          declineText={"Cancel"}
          isLoading={DayBannersStore.isManaging}
          onDecline={close}
          onAccept={this.deleteImage(imageId, close)}
        />
      ),
    });
  };

  deleteImage = (id: number, close: () => void) => async () => {
    const { setMessage } = this.props;

    try {
      await DayBannersStore.deleteImage(id);
      close();
      DayBannersStore.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onDragEnd = ({ dayTypeId }: { dayTypeId: number }) => async (
    result: DropResult,
  ) => {
    const { setMessage } = this.props;

    this.onDragStop();

    // dropped outside the list
    if (
      !result.destination ||
      !result.source ||
      result.source.index === result.destination.index
    ) {
      return;
    }

    const startIndex = result.source.index;
    const endIndex = result.destination.index;

    try {
      await DayBannersStore.reorderImages({ dayTypeId, startIndex, endIndex });
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onDragStart = () => {
    this.isDraggingGlobal = true;
  };

  onDragStop = () => {
    this.isDraggingGlobal = false;
  };

  resetIsPopupDirty = () => {
    this.isPopupDirty = false;
  };

  shouldShowConfirmation = () => {
    return this.isPopupDirty;
  };

  render() {
    return (
      <Container header={<Header controls={<PublishButton />} />}>
        <Page
          topControls={
            <BackButtonWrapper>
              <Button
                color="secondary"
                variant="text"
                startIcon={<KeyboardBackspaceIcon />}
                onClick={this.onBackClick}
              >
                Back to Today
              </Button>
            </BackButtonWrapper>
          }
          isLoading={DayBannersStore.isLoading}
        >
          <>
            {DayBannersStore.isReordering && <OverflowLoader />}
            {this.dayBanners.map(({ id, name, images }) => (
              <DayContainer key={`dayKey:${id}`}>
                <ContainerHeader>
                  <Wrapper>
                    <Typography variant="h3" color="textPrimary">
                      {name}
                    </Typography>
                  </Wrapper>
                  <Wrapper>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={this.onAddImage({
                        dayTypeId: id,
                        dayTypeName: name,
                      })}
                    >
                      Add Image
                    </Button>
                  </Wrapper>
                </ContainerHeader>
                <Divider />
                {images.length ? (
                  <ScrollContainer>
                    <DragDropContext
                      onDragStart={this.onDragStart}
                      onDragEnd={this.onDragEnd({ dayTypeId: id })}
                      isDraggingGlobal={this.isDraggingGlobal}
                    >
                      <Droppable droppableId="droppable" direction="horizontal">
                        {(provided, snapshot) => (
                          <Images
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            isDraggingOver={snapshot.isDraggingOver}
                          >
                            {images.map((image, index) => (
                              <Draggable
                                key={image.id}
                                draggableId={image.id.toString()}
                                index={index}
                              >
                                {(provided, snapshot) => (
                                  <ImageContainer
                                    ref={provided.innerRef}
                                    image={image.imageUrl}
                                    isDragging={snapshot.isDragging}
                                    withIconControls={!this.isDraggingGlobal}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                  >
                                    <IconControlsContainer>
                                      <IconButton
                                        onClick={this.onDelete({
                                          imageId: image.id,
                                        })}
                                        size="small"
                                      >
                                        <DeleteOutlineIcon />
                                      </IconButton>

                                      <IconButton
                                        onClick={this.onEditImage({
                                          dayTypeName: name,
                                          imageId: image.id,
                                          imageUrl: image.imageUrl,
                                        })}
                                        size="small"
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    </IconControlsContainer>
                                  </ImageContainer>
                                )}
                              </Draggable>
                            ))}

                            {provided.placeholder}
                          </Images>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </ScrollContainer>
                ) : (
                  <Placeholder
                    height={132}
                    width={132}
                    text={"No images added yet."}
                  />
                )}
              </DayContainer>
            ))}
          </>
        </Page>
      </Container>
    );
  }
}

export default withGlobalPopup(withGlobalSnackbar(withRouter(DayBanners)));
